import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { PricingChangeEntry } from '@larki/shared/type/pricing';

import Icon from '../../../components/icon/Icon';
import Loading from '../../../components/loading/Loading';
import { Button } from '../../../components/button';
import { formatDate, formatTime } from '../../../utilities/date';
import {
  useGetPricingHistoryQuery,
  useRevertPricingChangeMutation,
} from '../../../redux/rtk';
import {
  alertError,
  alertSuccess,
  alertWarning,
} from '../../../redux/actions';
import text from '../../../text';
import { captureError } from '../../../utilities/error';
import ConfirmModal from './ConfirmModal';
import { addOnVariantLabel, formatMultiplier } from './addOnLabels';
import { formatMoney } from './money';

const describeDiffs = (entry: PricingChangeEntry, categoryName?: string) =>
  entry.rows.flatMap((diff) => {
    if ('variant' in diff) {
      return diff.oldMultiplier !== diff.newMultiplier
        ? [
            `${addOnVariantLabel(categoryName, diff.variant)}: ${formatMultiplier(diff.oldMultiplier)} → ${formatMultiplier(diff.newMultiplier)}`,
          ]
        : [];
    }
    const lines: string[] = [];
    if (diff.oldPrice !== diff.newPrice) {
      lines.push(
        text('historyPriceDiff', {
          area: String(diff.oldArea),
          oldPrice: formatMoney(diff.oldPrice),
          newPrice: formatMoney(diff.newPrice),
        })
      );
    }
    if (diff.oldArea !== diff.newArea) {
      lines.push(
        text('historyAreaDiff', {
          oldArea: String(diff.oldArea),
          newArea: String(diff.newArea),
        })
      );
    }
    return lines;
  });

// The exact values reverting will apply, shown in the confirm modal.
const revertedValuesSummary = (entry: PricingChangeEntry, categoryName?: string) =>
  entry.rows
    .map((diff) =>
      'variant' in diff
        ? `${addOnVariantLabel(categoryName, diff.variant)}: ${formatMultiplier(diff.oldMultiplier)}`
        : `${diff.oldArea} m²: ${formatMoney(diff.oldPrice)}`
    )
    .join(', ');

type PricingHistoryProps = {
  categoryId: number;
  categoryName?: string;
};

const PricingHistory = ({ categoryId, categoryName }: PricingHistoryProps) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  // The section is collapsed by default; don't fetch until it's expanded.
  const { data: entries } = useGetPricingHistoryQuery(categoryId, {
    skip: !open,
  });
  const [revertPricingChange, { isLoading: isReverting }] =
    useRevertPricingChangeMutation();

  const [revertTarget, setRevertTarget] = useState<PricingChangeEntry | null>(
    null
  );

  const handleRevert = async () => {
    if (!revertTarget) return;
    try {
      const { applied } = await revertPricingChange(revertTarget.id).unwrap();
      setRevertTarget(null);
      dispatch(
        applied
          ? alertSuccess(text('pricingReverted'), {
              icon: 'circled-tick',
              className: 'pricing-modal-alert',
            })
          : alertWarning(text('revertNothingToRevert'), {
              className: 'pricing-modal-alert',
            })
      );
    } catch (err) {
      captureError(err);
      dispatch(alertError(text('pricingRevertFailed')));
    }
  };

  return (
    <div className='pricing-history'>
      <button
        type='button'
        className='pricing-history__toggle'
        onClick={() => setOpen((prev) => !prev)}
      >
        <Icon icon={open ? 'caret-down' : 'caret-right'} />
        <span>{text('changeHistory')}</span>
      </button>
      {open &&
        (!entries ? (
          <Loading />
        ) : entries.length === 0 ? (
          <p className='pricing-history__empty font-grey'>
            {text('noChangesYet')}
          </p>
        ) : (
          <ul className='pricing-history__list'>
            {entries.map((entry) => {
              const diffLines = describeDiffs(entry, categoryName);
              return (
                <li key={entry.id} className='pricing-history__entry'>
                  <div className='pricing-history__meta'>
                    <span>
                      {formatDate(entry.createdAt)}{' '}
                      {formatTime(entry.createdAt)}
                    </span>
                    <span>
                      {entry.user.fullName ??
                        entry.user.email ??
                        text('historyFormerStaff')}
                    </span>
                    {entry.revertedChangeId != null && (
                      <span className='pricing-history__revert-label mt-1'>
                        {text('revertAction')}
                      </span>
                    )}
                  </div>
                  <div className='pricing-history__diffs'>
                    {diffLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                    {diffLines.length === 0 && (
                      <div className='font-grey'>{text('revertNoEffect')}</div>
                    )}
                  </div>
                  <Button
                    variant='light'
                    className='pricing-history__revert-button'
                    onClick={() => setRevertTarget(entry)}
                  >
                    {text('revert')}
                  </Button>
                </li>
              );
            })}
          </ul>
        ))}
      <ConfirmModal
        show={revertTarget !== null}
        message={
          revertTarget
            ? text('revertWarning', {
                values: revertedValuesSummary(revertTarget, categoryName),
              })
            : ''
        }
        loading={isReverting}
        onHide={() => setRevertTarget(null)}
        onConfirm={handleRevert}
      />
    </div>
  );
};

export default PricingHistory;
